import React, { useEffect, useMemo, useState } from 'react';

const isEditable = (el) => {
  if (!el || !el.tagName) return false;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName) || el.isContentEditable;
};

/**
 * Wraps premium content (mock tests, courses) with anti-copy / anti-capture
 * deterrents: selection + copy + right-click + print blocking, devtools
 * shortcut blocking, PrintScreen clipboard wipe, a tab-switch blackout
 * overlay, and a tiled per-user watermark so any leaked capture is traceable.
 */
export function ProtectedContent({ children, watermark = '', className = '' }) {
  const [obscured, setObscured] = useState(false);
  const [warning, setWarning] = useState('');

  useEffect(() => {
    let warnTimer = null;
    const warn = (msg) => {
      setWarning(msg);
      clearTimeout(warnTimer);
      warnTimer = setTimeout(() => setWarning(''), 2200);
    };
    const block = (e) => {
      if (isEditable(e.target)) return;
      e.preventDefault();
      warn('This content is protected');
    };
    const onKeyDown = (e) => {
      const k = (e.key || '').toLowerCase();
      const mod = e.ctrlKey || e.metaKey;
      if (e.key === 'F12' || (mod && e.shiftKey && ['i', 'j', 'c'].includes(k)) || (mod && ['p', 's', 'u'].includes(k))) {
        e.preventDefault();
        warn('This action is disabled on protected content');
        return;
      }
      if (mod && ['c', 'x'].includes(k) && !isEditable(e.target)) {
        e.preventDefault();
        warn('Copying is disabled');
      }
    };
    const onKeyUp = (e) => {
      if (e.key === 'PrintScreen') {
        try { navigator.clipboard.writeText(''); } catch { /* clipboard unavailable */ }
        warn('Screenshots of this content are not allowed');
      }
    };
    const onVisibility = () => setObscured(document.hidden);
    document.addEventListener('contextmenu', block);
    document.addEventListener('copy', block);
    document.addEventListener('cut', block);
    document.addEventListener('dragstart', block);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearTimeout(warnTimer);
      document.removeEventListener('contextmenu', block);
      document.removeEventListener('copy', block);
      document.removeEventListener('cut', block);
      document.removeEventListener('dragstart', block);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const marks = useMemo(() => Array.from({ length: 28 }, (_, i) => i), []);
  const label = watermark ? `${watermark} · CELPIPMax` : 'CELPIPMax';

  return (
    <div className={`cmx-noselect relative ${className}`} data-testid="protected-content">
      {children}
      {/* Tiled watermark — deters screenshots / screen recordings */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden" style={{ zIndex: 2147483000 }} aria-hidden="true" data-testid="content-watermark">
        <div style={{ position: 'absolute', inset: '-25%', display: 'flex', flexWrap: 'wrap', gap: '110px 80px', alignContent: 'space-between', justifyContent: 'space-between', transform: 'rotate(-24deg)', opacity: 0.045 }}>
          {marks.map((i) => (
            <span key={i} style={{ fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap', color: '#000' }}>{label}</span>
          ))}
        </div>
      </div>
      {obscured && (
        <div className="fixed inset-0 flex items-center justify-center bg-neutral-950" style={{ zIndex: 2147483001 }} data-testid="content-hidden-overlay">
          <p className="text-white/70 text-sm font-medium px-6 text-center">Content hidden — return to this tab to continue.</p>
        </div>
      )}
      {warning && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-neutral-900 text-white text-xs font-semibold shadow-lg" style={{ zIndex: 2147483002 }} data-testid="content-protect-warning">
          {warning}
        </div>
      )}
    </div>
  );
}
